import { store, type AuthSession } from '@updater/auth';
import { getUuid } from '@updater/identity';

import { getGuestSession, guestAuthStore } from './guest-auth-store';

/**
 * Mirrors what `UpdaterAuthProvider` passes to `AuthProvider`. The mint happens
 * outside React (from an Apollo link), so the values can't be read from
 * `AuthContext` — keep them in sync with `providers/updater-auth-provider.tsx`.
 */
const AUTH_HEADERS = { app: 'mover', name: 'consumer-app' } as const;

/**
 * Shared across concurrent callers so the first guest request triggers exactly
 * one `createPublicSession`, and the rest wait on it rather than minting their
 * own. Cleared when it settles so a later request can retry after a failure.
 */
let pending: Promise<AuthSession | undefined> | undefined;

async function mintGuestSession(url: string): Promise<AuthSession | undefined> {
  try {
    // signIn resolves either way — it routes failures into the request atom
    // rather than rejecting — so success is decided by reading the store back.
    await store.signIn(
      {
        url,
        headers: { ...AUTH_HEADERS },
        type: 'public',
        input: { userUuid: getUuid() },
      },
      guestAuthStore
    );
  } catch (error) {
    console.error('[guest-auth] createPublicSession threw', error);
    return undefined;
  }

  const session = getGuestSession();

  if (!session) {
    const request = guestAuthStore.get(store.requestAtom);
    console.error(
      '[guest-auth] createPublicSession failed',
      'error' in request ? request.error : undefined
    );
  }

  return session;
}

/**
 * Resolves the guest session, minting one on first call.
 *
 * Never rejects: if `createPublicSession` fails the error is logged and this
 * resolves undefined, so the caller proceeds without the session headers
 * rather than failing the request outright.
 *
 * Returns an already-minted session synchronously-ish (a resolved promise),
 * which covers the session rehydrated by `persistGuestAuth()` — a reload
 * reuses it instead of minting a second one.
 */
export function ensureGuestSession(
  url: string
): Promise<AuthSession | undefined> {
  // getUuid() reads document.cookie and location — browser only.
  if (typeof window === 'undefined') return Promise.resolve(undefined);

  const existing = getGuestSession();
  if (existing) return Promise.resolve(existing);

  pending ??= mintGuestSession(url).finally(() => {
    pending = undefined;
  });

  return pending;
}
